// Utility functions for Zebra printer integration

// Debug logger
const DEBUG = true;
const logDebug = (message, data = null) => {
  if (DEBUG) {
    console.log(`🖨️ Printer Debug: ${message}`);
    if (data) {
      console.log(data);
    }
  }
};

/**
 * Check if the BrowserPrint library is available
 * @returns {boolean} Whether the BrowserPrint library is loaded
 */
export const isBrowserPrintAvailable = () => {
  return typeof BrowserPrint !== "undefined";
};

/**
 * Get available Zebra printers
 * @returns {Promise<Array>} Array of printers or empty array if none found
 */
export const getZebraPrinters = () => {
  return new Promise((resolve, reject) => {
    if (!isBrowserPrintAvailable()) {
      reject(new Error("Zebra Browser Print is not loaded"));
      return;
    }

    BrowserPrint.getLocalDevices(
      (devices) => {
        const printers = devices.filter((d) => d.deviceType === "printer");
        logDebug(`Found ${printers.length} printer(s)`);
        printers.forEach((printer, index) => {
          logDebug(`  [${index}] ${printer.name} (UID: ${printer.uid})`);
        });
        resolve(printers);
      },
      (error) => {
        logDebug("Error finding printers", error);
        reject(error);
      },
      "printer"
    );
  });
};

/**
 * Get a specific printer by UID
 * @param {string} uid - The UID of the printer to find
 * @returns {Promise<object|null>} The printer object or null if not found
 */
export const getPrinterByUid = async (uid) => {
  try {
    const printers = await getZebraPrinters();
    const printer = printers.find(p => p.uid === uid);
    if (printer) {
      logDebug(`Found printer by UID ${uid}:`, printer.name);
    } else {
      logDebug(`Printer with UID ${uid} not found`);
    }
    return printer || null;
  } catch (error) {
    logDebug("Error getting printer by UID:", error);
    throw error;
  }
};

/**
 * Print ZPL code to the specified printer
 * @param {string} zplCode - The ZPL code to print
 * @param {object} printer - The printer object to use (optional, uses first available if not specified)
 * @returns {Promise<string>} Success message or error
 */
export const printZPL = (zplCode, printer = null) => {
  return new Promise((resolve, reject) => {
    if (!isBrowserPrintAvailable()) {
      reject(new Error("Zebra Browser Print is not loaded"));
      return;
    }

    if (printer) {
      // If printer is provided, use it directly
      printer.send(
        zplCode,
        () => {
          logDebug(`Print job sent to ${printer.name}`);
          resolve(`Print job sent successfully to ${printer.name}`);
        },
        (error) => {
          logDebug("Print failed", error);
          reject(error);
        }
      );
    } else {
      // Find a printer first
      getZebraPrinters()
        .then((printers) => {
          if (printers.length === 0) {
            reject(new Error("No printers available"));
            return;
          }

          const selectedPrinter = printers[0]; // Use first printer

          selectedPrinter.send(
            zplCode,
            () => {
              logDebug(`Print job sent to ${selectedPrinter.name}`);
              resolve(`Print job sent successfully to ${selectedPrinter.name}`);
            },
            (error) => {
              logDebug("Print failed", error);
              reject(error);
            }
          );
        })
        .catch((error) => {
          reject(error);
        });
    }
  });
};

/**
 * Send job IDs to the server to get ZPL code
 * @param {string} jobIds - The job IDs to send
 * @param {string} stickerType - Which stickers to print ("all" | "float_frame" | "stretcher_bar")
 * @returns {Promise<{zpl: string, skipped: Array<{order_item: string, reason: string}>}>}
 */
export const getZPLForJobIds = async (jobIds, stickerType = "all") => {
  try {
    const response = await fetch("/print", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
          ?.content,
      },
      body: JSON.stringify({ job_ids: jobIds, sticker_type: stickerType }),
    });

    if (!response.ok) {
      throw new Error(`Server responded with status: ${response.status}`);
    }

    const data = await response.json();
    return { zpl: data.zpl || "", skipped: data.skipped || [] };
  } catch (error) {
    logDebug("Error getting ZPL code", error);
    throw error;
  }
};

export default {
  isBrowserPrintAvailable,
  getZebraPrinters,
  getPrinterByUid,
  printZPL,
  getZPLForJobIds,
};
